<script>
	import CustomBreadcrumb from "src/components/CustomBreadcrumb/CustomBreadcrumb.svelte";
	import CustomFileInput from "../../components/CustomFileInput/CustomFileInput.svelte";
	import { brand } from "src/brand";
	import { getMasterData } from "src/services/profile";
	import { joinErrorMessages } from "src/services/helpers";
	import { extractErrorMessage, truncateToDp } from "src/utils/misc";
	import { filesToBase64 } from "../../utils/fileUpload";
	import { onMount } from "svelte";
	import { field, form } from "svelte-forms";
	import { max, required } from "svelte-forms/validators";
	import { navigate } from "svelte-routing";
	import Select from "svelte-select";
	import { Button, Col, Row } from "@sveltestrap/sveltestrap";
	import SveltyPicker from "svelty-picker";
	import CustomInput from "../../components/CustomInput/CustomInput.svelte";
	import CustomSelect from "../../components/CustomSelect/CustomSelect.svelte";
	import ErrorMessageHandler from "../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import Loader from "../../components/Loader/Loader.svelte";
	import PageHeader from "../../components/PageHeader/PageHeader.svelte";
	import client from "../../services/client";
	import Lightbox from "../../components/Lightbox/Lightbox.svelte";
	import {
		carparkDetail,
		errorMessage,
		imageLightboxIndex,
		isLightBoxOpen,
		loading,
		toastMessage,
	} from "../../store/store";

	export let isEdit;
	export let descriptionMaxLength = 250;

	let selectedAddress = "0";
	let carParkOwnerOptions = [];
	let existingImages = [];
	let newFiles = [];

	$: displayFiles = [
		...existingImages.map((img) => ({
			file: img.fileUrl || img.fileData,
			canDelete: true,
		})),
		...newFiles.map((f) => ({ file: f, canDelete: true })),
	];

	const validateCoordinates = (min, max) => (value) => {
		const valid =
			typeof value === "string" &&
			/^-?\d+\.\d{6}$/.test(value) &&
			parseFloat(value) >= min &&
			parseFloat(value) <= max;
		return { valid, name: "invalid" };
	};

	let carParkId = field("carParkId", "", [required()]);
	let description = field("description", "", [
		required(),
		max(descriptionMaxLength),
	]);
	let activeFrom = field("activeFrom", "", [required()]);
	let activeTo = field("activeTo", "", [required()]);
	let carParkOwnerId = field("carParkOwnerId", "", [required()]);
	let address = field("address", "", [required()]);
	let latitude = field(
		"latitude",
		"",
		brand.isCarparkCoordinatesEnabled
			? [required(), validateCoordinates(-90, 90)]
			: []
	);
	let longitude = field(
		"longitude",
		"",
		brand.isCarparkCoordinatesEnabled
			? [required(), validateCoordinates(-180, 180)]
			: []
	);
	let postalCode = field("postalCode", "", []);

	async function getCarParkOwnerOptions() {
		try {
			const {
				data: { carParkOwners },
			} = await getMasterData();
			carParkOwnerOptions = carParkOwners;

			if (brand.defaultCarParkOwner && !isEdit) {
				const defaultOwner = carParkOwners.find(
					(o) => o.name === brand.defaultCarParkOwner
				);
				if (defaultOwner) {
					$carParkOwnerId.value = defaultOwner.id;
				}
			}
		} catch (err) {
			extractErrorMessage(err);
		}
	}

	onMount(() => {
		if (isEdit) {
			$carParkId.value = $carparkDetail.carParkId;
			$description.value = $carparkDetail.description;
			$activeFrom.value = $carparkDetail.activeFrom;
			$activeTo.value = $carparkDetail.activeTo;
			$carParkOwnerId.value = $carparkDetail.carParkOwnerId;
			$address.value = $carparkDetail.address;
			$latitude.value = $carparkDetail.latitude;
			$longitude.value = $carparkDetail.longitude;
			$postalCode.value = $carparkDetail.postalCode;
			existingImages = $carparkDetail.carParkImages || [];
		}
		getCarParkOwnerOptions();
	});

	const myForm = form(
		carParkId,
		description,
		activeFrom,
		activeTo,
		carParkOwnerId,
		address,
		latitude,
		longitude,
		postalCode
	);

	function extractCarparkErrorMessage(error) {
		const data = error?.response?.data;
		if (data && Object.values(data).some(Array.isArray)) {
			errorMessage.set(joinErrorMessages(data, error.response.status));
			return;
		}
		extractErrorMessage(error);
	}

	let duplicatecarparkId;
	async function handleSubmit() {
		if (selectedAddress == "0") {
			selectedAddress = "";
		}

		loading.set(true);

		const newImagesAsBase64 = await filesToBase64(newFiles);

		try {
			const data = {
				name: $carParkId.value,
				description: $description.value,
				postalCode: $postalCode.value,
				activeFrom: new Date($activeFrom.value),
				activeTo: new Date($activeTo.value),
				carParkOwnerId: $carParkOwnerId.value,
				address: $address.value.value,
				carParkImages: [...existingImages, ...newImagesAsBase64],
				longitude: $longitude.value,
				latitude: $latitude.value,
			};
			if (isEdit) {
				data.id = isEdit;
			}
			const res = await client("/v1/carParks", {
				method: isEdit ? "PUT" : "POST",
				version: "Vehicle",
				data,
			});

			if (res.status === 200) {
				if (isEdit) {
					toastMessage.set({
						message: "Changes saved",
						type: "success",
					});
					navigate(`/carpark-management/details/${res.data.id}`);
				} else {
					toastMessage.set({
						message: "Car park created successfully",
						type: "success",
					});
					navigate("/carpark-management");
				}
			}

			loading.set(false);
		} catch (error) {
			loading.set(false);
			extractCarparkErrorMessage(error);
		}
	}

	function onFileAdded(e) {
		const added = Array.from(e.target.files);
		newFiles = [...newFiles, ...added];
	}

	function onRemoveFile({ detail: { index } }) {
		if (index < existingImages.length) {
			existingImages = existingImages.filter((_, i) => i !== index);
		} else {
			const newIndex = index - existingImages.length;
			newFiles = newFiles.filter((_, i) => i !== newIndex);
		}
	}

	function restrictCoordChars(e) {
		if (e.metaKey || e.ctrlKey) return;
		const char = e.key;
		if (/[0-9]/.test(char)) return;
		if (
			char === "-" &&
			e.target.selectionStart === 0 &&
			!e.target.value.includes("-")
		)
			return;
		if (char === "." && !e.target.value.includes(".")) return;
		e.preventDefault();
	}

	function handleLongCoords(e) {
		const string = (e.clipboardData || window.clipboardData).getData(
			"text"
		);
		const parts = string.trim().split(/[,\s]+/);
		if (parts.length === 2) {
			e.preventDefault();
			const [lat, lng] = parts;
			if (lat) $latitude.value = truncateToDp(lat, 6);
			if (lng) $longitude.value = truncateToDp(lng, 6);
		}
	}

	let load = false;
	loading.subscribe((value) => {
		load = value;
	});

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	const loadCarparkOptions = async (filterText) => {
		return await brand.carparkAddress(filterText);
	};

	function handleSelect(event) {
		selectedAddress = event.detail.value;
		const resolvedLongitude =
			event.detail.longitude || $carparkDetail.longitude;
		const resolvedLatitude =
			event.detail.latitude || $carparkDetail.latitude;
		$longitude.value =
			resolvedLongitude != null ? truncateToDp(resolvedLongitude, 6) : "";
		$latitude.value =
			resolvedLatitude != null ? truncateToDp(resolvedLatitude, 6) : "";
		$postalCode.value = event.detail.postal || $carparkDetail.postalCode;
	}
</script>

{#if $isLightBoxOpen}
	<Lightbox
		on:click={() => isLightBoxOpen.set(false)}
		imageShowingIndex={$imageLightboxIndex}
	/>
{/if}

{#if isEdit}
	<CustomBreadcrumb
		links={[
			{ label: "Car park management", to: "/carpark-management" },
			{ label: "Edit car park" },
		]}
	/>
{/if}

<Row>
	<Col>
		<Col xs={6}>
			<ErrorMessageHandler message={err} />
		</Col>
		<div>
			<PageHeader title={isEdit ? "Edit carpark" : "Add new car park"} />
			<Col>
				<Row>
					<Col>
						<label for="carParkId" class="d-block mt-3">
							<span>
								Car park ID<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={3}>
						<CustomInput
							type="text"
							bind:value={$carParkId.value}
							error={$myForm.hasError("carParkId.required") ||
								duplicatecarparkId}
							errorText="Car park id is required"
						/>
					</Col>
				</Row>
			</Col>
			<Col>
				<Row>
					<Col>
						<label for="priority" class="d-block mt-3">
							<span>
								Address<span class="text-danger">*</span></span
							>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<Select
							loadOptions={loadCarparkOptions}
							containerClasses="form-select getgo select"
							id="food"
							inputAttributes={{ "aria-label": "Address" }}
							hasError={$myForm.hasError("address.required")}
							on:select={handleSelect}
							placeholder={brand.carparkAddressPlaceholder}
							bind:value={$address.value}
						/>

						{#if $myForm.hasError("address.required")}
							<div>
								<p class="error-text">Address is required</p>
							</div>
						{/if}
					</Col>
				</Row>
			</Col>

			<Col>
				<Row>
					<Col>
						<label for="description" class="d-block mt-3">
							<span>
								Description<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<div class="description-textarea">
							<CustomInput
								type="textarea"
								bind:value={$description.value}
								maxlength={descriptionMaxLength}
								placeholder="Describe where the car park is"
								error={$myForm.hasError(
									"description.required"
								) || $myForm.hasError("description.max")}
								errorText={$myForm.hasError(
									"description.required"
								)
									? "Description is required"
									: $myForm.hasError("description.max")
										? `Maximum ${descriptionMaxLength} characters`
										: ""}
							/>
							<div class="char-counter">
								{($description.value || "")
									.length}/{descriptionMaxLength}
							</div>
						</div>
					</Col>
				</Row>
			</Col>

			{#if brand.isCarparkCoordinatesEnabled}
				<Col>
					<Row>
						<Col xs={3}>
							<label for="latitude" class="d-block mt-3">
								Latitude<span class="text-danger">*</span>
							</label>
						</Col>
						<Col xs={3}>
							<label for="longitude" class="d-block mt-3">
								Longitude<span class="text-danger">*</span>
							</label>
						</Col>
					</Row>
					<Row>
						<Col xs={3} class="coord-field">
							<CustomInput
								id="latitude"
								type="text"
								placeholder="e.g. 1.336307"
								bind:value={$latitude.value}
								keypress={restrictCoordChars}
								onPaste={handleLongCoords}
								disabled={!$address.value}
								error={!$myForm.valid &&
									($myForm.hasError("latitude.required") ||
										$myForm.hasError("latitude.invalid"))}
								errorText="Please input a valid latitude with 6 d.p. between -90 and 90 e.g. -80.123456"
							/>
							<span class="text-muted ms-1 coord-caption"
								>Between -90 and 90</span
							>
						</Col>
						<Col xs={3} class="coord-field">
							<CustomInput
								id="longitude"
								type="text"
								placeholder="e.g. 103.697534"
								bind:value={$longitude.value}
								keypress={restrictCoordChars}
								onPaste={handleLongCoords}
								disabled={!$address.value}
								error={!$myForm.valid &&
									($myForm.hasError("longitude.required") ||
										$myForm.hasError("longitude.invalid"))}
								errorText="Please input a valid longitude with 6 d.p. between -180 and 180 e.g. -130.574382"
							/>
							<span class="text-muted ms-1 coord-caption"
								>Between -180 and 180</span
							>
						</Col>
					</Row>
				</Col>
			{/if}

			<Col>
				<Row>
					<Col>
						<label for="carparkOwner" class="d-block mt-3">
							<span>
								Car park owner<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<CustomSelect
							bind:options={carParkOwnerOptions}
							type="text"
							bind:value={$carParkOwnerId.value}
							error={$myForm.hasError("carParkOwnerId.required")}
							errorText="Carpark owner is required"
							defaultOption="Select carpark owner"
						/>
					</Col>
				</Row>
			</Col>

			<Col>
				<Row>
					<Col>
						<div class="d-block mt-3 pb-1">Photo of car park</div>
					</Col>
				</Row>
				<Row>
					<Col xs={6}>
						<div class="file-input-no-gap">
							<CustomFileInput
								fileType="image"
								name="carParkImage"
								files={displayFiles}
								handleFileChange={onFileAdded}
								on:remove={onRemoveFile}
								isEdit={true}
								maxFileSize={2 * 1024 * 1024}
							/>
						</div>
					</Col>
				</Row>
			</Col>

			<Col>
				<Row>
					<Col xs={3}>
						<label for="lastServicedate" class="d-block mt-3">
							<span>
								Start date<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
					<Col xs={3}>
						<label for="lastCleaned" class="d-block mt-3">
							<span>
								End date<span class="text-danger">*</span>
							</span>
						</label>
					</Col>
				</Row>
				<Row>
					<Col xs={3}>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'activeFrom.required'
							)
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$activeFrom.value}
							on:change={$activeFrom.validate}
						/>
						{#if $myForm.hasError("activeFrom.required")}
							<div>
								<p class="error-text">Start date is required</p>
							</div>
						{/if}
					</Col>
					<Col xs={3}>
						<SveltyPicker
							inputClasses="form-control getgo input {$myForm.hasError(
								'activeTo.required'
							)
								? 'border border-danger'
								: ''}"
							format="yyyy-mm-dd"
							placeholder="YYYY-MM-DD"
							bind:value={$activeTo.value}
							on:change={$activeTo.validate}
						/>
						{#if $myForm.hasError("activeTo.required")}
							<div>
								<p class="error-text">End date is required</p>
							</div>
						{/if}
					</Col>
				</Row>
			</Col>

			<Row>
				<Col class="d-flex justify-content-end gap-3">
					<Button class="themeButton" on:click={() => myForm.reset()}
						>Reset all fields</Button
					>
					<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
					<span
						class="d-inline-block"
						data-bs-toggle={!$myForm.valid ? "tooltip" : ""}
						title={!$myForm.valid ? "Fill all required field" : ""}
					>
						<Button
							class="themeButton primary"
							on:click={() => handleSubmit()}
							on:click={$myForm.validate}
							disabled={!$myForm.valid ||
								!$carParkId.value ||
								!$description.value ||
								!$activeFrom.value ||
								!$activeTo.value ||
								!$carParkOwnerId.value ||
								!$address.value ||
								(brand.isCarparkCoordinatesEnabled &&
									(!$latitude.value || !$longitude.value)) ||
								!$postalCode.value}
						>
							{#if load}
								<Loader />
							{:else if isEdit}
								Save
							{:else}
								Add car park
							{/if}
						</Button>
					</span>
				</Col>
			</Row>
		</div>
	</Col>
</Row>

<style>
	:global(.coord-field .error-text) {
		display: block;
		white-space: normal !important;
		text-wrap: wrap !important;
		font-size: 11px !important;
		margin-left: 0 !important;
	}

	.coord-caption {
		font-size: 11px;
	}

	.description-textarea :global(.form-control) {
		height: 140px;
		min-height: 140px;
		resize: vertical;
	}

	.char-counter {
		text-align: right;
		font-size: 12px;
		color: var(--fds-on-surface-variant);
		margin-top: 2px;
	}

	.file-input-no-gap :global(.my-3) {
		margin-top: 0 !important;
	}

	.file-input-no-gap :global(.my-2) {
		display: none !important;
	}
</style>
